// Shared rental-agreement multi-select logic. Used by the guest signing page and its
// client-side PDF serializer (guest-portal frontend) and by the server-side PDF fallback
// and finalize validation (backend). Centralizing the selection rules keeps the client
// completion gate and the server finalize check from ever disagreeing (which would show
// the guest "complete" and then reject the submit with a 400), and centralizing the PDF
// markup + wrapper matching keeps both PDF paths rendering identically. BES-8826.
/**
 * Resolve the effective min/max selection bounds for a field.
 * - Single choice (radio): ignores any configured min/max range and permits at most one
 *   selection, so a stale range left over from multiple mode can never block completion.
 * - Multiple: required forces at least 1; an empty max means unbounded, but a configured
 *   0 is treated as a real limit.
 */
export function multiSelectBounds(field) {
    if (field.multiple === false) {
        return { min: field.required ? 1 : 0, max: 1 };
    }
    const parsedMin = field.min ? Number(field.min) : 0;
    const safeMin = Number.isFinite(parsedMin) ? parsedMin : 0;
    const min = field.required ? Math.max(1, safeMin) : safeMin;
    const hasMax = field.max !== '' && field.max != null;
    const parsedMax = hasMax ? Number(field.max) : NaN;
    const max = hasMax && Number.isFinite(parsedMax) ? parsedMax : null;
    return { min, max };
}
/**
 * Count distinct selected values that are still valid configured options. Mirrors finalize,
 * which dedupes and drops anything not in the allowed set before checking min/max, so stale
 * strings left in restored progress after the host edits options can neither satisfy the
 * minimum nor overfill the maximum.
 */
export function countValidSelections(values, allowedOptions) {
    const allowedSet = new Set(allowedOptions);
    const vals = Array.isArray(values) ? values : [];
    return new Set(vals.filter((v) => allowedSet.has(v))).size;
}
/** True when a field's current selection satisfies its bounds. */
export function isMultiSelectComplete(field, values, allowedOptions) {
    const count = countValidSelections(values, allowedOptions);
    const { min, max } = multiSelectBounds(field);
    if (count < min)
        return false;
    if (max !== null && count > max)
        return false;
    return true;
}
/**
 * Find the `<` that opens the element containing the attribute at `attrIndex`, scanning
 * backward while skipping over quoted attribute values so a `<` inside an earlier attribute
 * (e.g. a label like "Age < 18" or an option containing "<div") is never mistaken for the tag
 * start. Returns -1 if none is found.
 */
export function findElementStart(html, attrIndex) {
    let i = attrIndex;
    while (i >= 0) {
        const ch = html[i];
        if (ch === '"' || ch === "'") {
            // Scanning backward, this is the value's closing quote; jump to its opening quote so the
            // value's contents (which may contain a raw `<`) are skipped entirely.
            i--;
            while (i >= 0 && html[i] !== ch)
                i--;
            i--;
            continue;
        }
        if (ch === '<')
            return i;
        i--;
    }
    return -1;
}
/**
 * Given the index of an attribute occurrence inside an element's opening tag, return the
 * [start, end) offsets of the enclosing balanced `<div>...</div>`. The start is located
 * quote-aware (findElementStart) and the forward walk tracks tag/quote state, so `<div` and
 * `</div>` substrings inside quoted attribute values (e.g. data-options or data-label text)
 * neither mislocate the start nor miscount the depth. Returns null when the wrapper cannot be
 * bounded.
 */
export function findEnclosingBalancedDiv(html, attrIndex) {
    const start = findElementStart(html, attrIndex);
    if (start === -1)
        return null;
    let depth = 0;
    let i = start;
    let inTag = false;
    let quote = null;
    while (i < html.length) {
        const ch = html[i];
        if (quote) {
            if (ch === quote)
                quote = null;
            i++;
        }
        else if (inTag) {
            if (ch === '"' || ch === "'")
                quote = ch;
            else if (ch === '>')
                inTag = false;
            i++;
        }
        else if (html.substring(i, i + 4) === '<div' && /[\s/>]/.test(html[i + 4] || '>')) {
            depth++;
            inTag = true;
            i += 4;
        }
        else if (html.substring(i, i + 6) === '</div>') {
            depth--;
            if (depth === 0)
                return { start, end: i + 6 };
            i += 6;
        }
        else if (ch === '<') {
            inTag = true;
            i++;
        }
        else {
            i++;
        }
    }
    return null;
}
const decodeHtml = (str) => str
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&amp;/g, '&');
const escapeHtml = (str) => str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
/**
 * Render the borderless signed-PDF markup for a multi-select field: the question followed
 * by a checked/unchecked list of options. Borderless so the field flows with the agreement
 * body, matching the live signing page. Shared by the client serializer and the server-side
 * fallback so both PDF paths stay identical.
 */
export function renderMultiSelectPdfHtml({ label, options, selected }) {
    const rowsHtml = options
        .map((opt) => {
        const isSel = selected.includes(opt);
        const icon = isSel
            ? '<span style="display:inline-flex;align-items:center;justify-content:center;width:18px;height:18px;background:#059669;border-radius:4px;color:white;font-size:12px;font-weight:bold;">&#10003;</span>'
            : '<span style="display:inline-flex;align-items:center;justify-content:center;width:18px;height:18px;background:#f1f5f9;border:2px solid #cbd5e1;border-radius:4px;"></span>';
        return `<div style="display:flex;align-items:center;gap:8px;font-size:14px;color:${isSel ? '#1e293b' : '#64748b'};">${icon}<span>${escapeHtml(decodeHtml(opt))}</span></div>`;
    })
        .join('');
    return `<div style="margin:12px 0;">
            <div style="font-weight:600;font-size:14px;margin-bottom:8px;">${escapeHtml(decodeHtml(label))}</div>
            <div style="display:flex;flex-direction:column;gap:6px;">${rowsHtml}</div>
          </div>`;
}
